// Клиент для `/api/public/tool` — каталог инструмента публичного юзера.
// id — Uuid (string), payload — Record<string, unknown> (externally-tagged
// enum `tool_library::Tool`); распаковка/упаковка в tool_kinds.ts.
//
// ⚠ НЕ мигрировано на @webtypes (намеренно — задокументированный drift):
//   1) ToolKind: генерёный союз несёт 2 лишних варианта (dv_collar/packer),
//      которых нет в landing-форме (KIND_BY_ID/TOOL_KINDS на локальном
//      `../tool_kind`). Импорт генерёного ToolDto затащил бы генерёный
//      ToolKind в `kind`, и он бы не присваивался к локальному союзу
//      ВЕЗДЕ по коду страниц (DrillstringPage/Schematic/ToolPage). Поэтому
//      ToolDto держим локальным на локальном ToolKind.
//   2) CreateToolBody/UpdateToolBody: серверные типы выросли полями
//      origin_state_uuid / origin_well_uuid / tags (+ clear_origin_state /
//      clear_origin_well для update), которых у landing нет ни в UI, ни в
//      форме, а генерёный body делает их ОБЯЗАТЕЛЬНЫМИ → call-site здесь их
//      не заполнить без новой фичи. Держим body-типы локально как
//      СОВМЕСТИМОЕ подмножество (origin/tags не шлём — сервер применит
//      дефолты). Мигрировать после: (а) синка `../tool_kind` с генерёным
//      союзом (+метадата dv_collar/packer в tool_kinds.ts) и (б) поддержки
//      origin/tags в landing-форме.

import type { ToolKind } from '../tool_kind'

export type ToolDto = {
  id: string
  kind: ToolKind
  title: string
  schema_version: number
  payload: Record<string, unknown>
  drilling_company_id: string | null
  created_at: string
  updated_at: string
}

export type CreateToolBody = {
  kind: ToolKind
  title: string
  payload: Record<string, unknown>
  drilling_company_id?: string | null
}

// Семантика drilling_company:
//  - поле опущено или null → не трогать;
//  - поле = uuid → выставить;
//  - clear_drilling_company: true → отвязать (приоритетнее чем id).
// Двухуровневое различение «не трогать»/«обнулить» — через флаг, чтобы не
// тащить double-Option на бэке.
export type UpdateToolBody = {
  kind?: ToolKind | null
  title?: string | null
  payload?: Record<string, unknown> | null
  drilling_company_id?: string | null
  clear_drilling_company?: boolean
}

const BASE = '/api/public/tool'

async function ensureOk(resp: Response): Promise<void> {
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`${resp.status} ${resp.statusText}${text ? ': ' + text : ''}`)
  }
}

export async function listTools(): Promise<ToolDto[]> {
  const resp = await fetch(BASE, { credentials: 'same-origin' })
  await ensureOk(resp)
  return resp.json()
}

export async function createTool(body: CreateToolBody): Promise<ToolDto> {
  const resp = await fetch(BASE, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

export async function updateTool(id: string, body: UpdateToolBody): Promise<ToolDto> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  await ensureOk(resp)
  return resp.json()
}

// 409 = деталь используется в компоновке (FK ON DELETE RESTRICT).
export class ToolInUseError extends Error {
  constructor() {
    super('Инструмент используется в компоновке — удалить нельзя')
  }
}

export async function deleteTool(id: string): Promise<void> {
  const resp = await fetch(`${BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  })
  if (resp.status === 409) throw new ToolInUseError()
  await ensureOk(resp)
}
